import { useState } from 'react';

import {
  dataTransferHasStoryIds,
  readStoryIdsDragPayload,
} from './dragPayload';

import type { DragEvent } from 'react';

type StoriesDroppedHandler = (
  storyIds: string[],
  folderId: string | null,
) => void;

/**
 * Shared drag-drop wiring for anything that accepts a dropped story selection
 * (folder rows, folder cards, sidebar tree nodes). Keeps the dragover/leave/drop
 * logic and the "is a valid drop hovering over me" state in one place so every
 * drop target behaves and looks identical.
 */
export function useStoryDropTarget(
  folderId: string | null,
  onStoriesDropped?: StoriesDroppedHandler,
) {
  const [isDropTarget, setIsDropTarget] = useState(false);

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (!onStoriesDropped) return;
    if (!dataTransferHasStoryIds(event.dataTransfer)) return;
    // preventDefault marks this element as a valid drop target.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (!isDropTarget) setIsDropTarget(true);
  };

  const handleDragLeave = () => {
    if (isDropTarget) setIsDropTarget(false);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (!onStoriesDropped) return;
    const ids = readStoryIdsDragPayload(event.dataTransfer);
    setIsDropTarget(false);
    if (!ids || ids.length === 0) return;
    event.preventDefault();
    onStoriesDropped(ids, folderId);
  };

  return {
    isDropTarget,
    dropHandlers: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
